import { useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { initialMyInvites } from "@/data/myInvites";
import {
  ANIMATION_LABELS,
  INVITE_STAGE_LABELS,
  INVITE_STAGE_ORDER,
  THEME_LABELS
} from "@/lib/myInvitesTypes";
import type {
  DraftInvite,
  InviteStage,
  MyInvite,
  PublishedInvite
} from "@/lib/myInvitesTypes";

type ToastState = {
  kind: "idle" | "copied" | "shared" | "downloaded" | "saved" | "published";
  text: string;
};

const stageTone: Record<InviteStage, string> = {
  draft: "bg-[rgb(140_89_242_/_0.14)] text-[rgb(96_67_175)]",
  created: "bg-[rgb(204_165_96_/_0.16)] text-[rgb(143_105_27)]",
  sent: "bg-[rgb(75_157_132_/_0.14)] text-[rgb(36_109_88)]",
  opened: "bg-[rgb(102_120_200_/_0.14)] text-[rgb(72_84_152)]",
  responded: "bg-[rgb(214_102_120_/_0.14)] text-[rgb(162_60_79)]"
};

const themeTone: Record<string, string> = {
  romantic: "from-rose-100 via-white to-amber-50",
  velvet: "from-rose-100 via-white to-fuchsia-50",
  festive: "from-amber-100 via-white to-pink-50",
  elegant: "from-zinc-100 via-white to-stone-50",
  minimal: "from-slate-100 via-white to-zinc-50",
  cosmic: "from-indigo-100 via-white to-fuchsia-50",
  classic: "from-stone-100 via-white to-stone-50",
  playful: "from-pink-100 via-white to-cyan-50",
  luxuryDark: "from-zinc-100 via-white to-amber-50",
  cinematic: "from-slate-100 via-white to-blue-50",
  funnyChaos: "from-lime-100 via-white to-rose-50"
};

const stageTabCopy = INVITE_STAGE_ORDER.map((stage) => ({
  stage,
  label: INVITE_STAGE_LABELS[stage]
}));

export default function Home() {
  const [items, setItems] = useState<MyInvite[]>(initialMyInvites);
  const [searchParams, setSearchParams] = useSearchParams();
  const [toast, setToast] = useState<ToastState>({
    kind: "idle",
    text: ""
  });
  const [editingDrafts, setEditingDrafts] = useState<Record<string, boolean>>({});

  const activeTab = normalizeStage(searchParams.get("tab"));
  const filteredItems = useMemo(
    () => items.filter((item) => item.status === activeTab),
    [activeTab, items]
  );
  const selectedId = searchParams.get("invite");
  const selectedItem =
    filteredItems.find((item) => item.id === selectedId) ?? filteredItems[0] ?? null;

  useEffect(() => {
    if (!selectedItem) return;
    if (selectedId !== selectedItem.id || searchParams.get("tab") !== activeTab) {
      const next = new URLSearchParams(searchParams);
      next.set("tab", activeTab);
      next.set("invite", selectedItem.id);
      setSearchParams(next, { replace: true });
    }
  }, [activeTab, searchParams, selectedId, selectedItem, setSearchParams]);

  useEffect(() => {
    document.title = "My Invites";
  }, []);

  useEffect(() => {
    if (toast.kind === "idle") return;
    const timer = window.setTimeout(() => {
      setToast({ kind: "idle", text: "" });
    }, 2200);
    return () => window.clearTimeout(timer);
  }, [toast.kind]);

  const counts = useMemo(
    () =>
      stageTabCopy.map(({ stage }) => ({
        stage,
        count: items.filter((item) => item.status === stage).length
      })),
    [items]
  );

  const selectTab = (stage: InviteStage) => {
    const nextItems = items.filter((item) => item.status === stage);
    const next = new URLSearchParams(searchParams);
    next.set("tab", stage);
    if (nextItems[0]) {
      next.set("invite", nextItems[0].id);
    } else {
      next.delete("invite");
    }
    setSearchParams(next);
  };

  const selectInvite = (inviteId: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("invite", inviteId);
    setSearchParams(next);
  };

  const updateInvite = (
    inviteId: string,
    updater: (invite: MyInvite) => MyInvite
  ) => {
    setItems((current) => current.map((item) => (item.id === inviteId ? updater(item) : item)));
  };

  const publishDraft = (draft: DraftInvite) => {
    const slugBase = slugify(`${draft.recipientName}-${draft.title}`);
    const slug = `${slugBase}-${Math.random().toString(36).slice(2, 6)}`;
    const publicUrl = `${window.location.origin}/i/${slug}`;
    const published: PublishedInvite = {
      ...draft,
      kind: "published",
      status: "created",
      slug,
      publicUrl
    };

    setItems((current) => current.map((item) => (item.id === draft.id ? published : item)));
    setToast({ kind: "published", text: "Invitation published and moved out of Drafts." });
    const next = new URLSearchParams(searchParams);
    next.set("tab", "created");
    next.set("invite", published.id);
    setSearchParams(next);
  };

  const deleteDraft = (draft: DraftInvite) => {
    setItems((current) => current.filter((item) => item.id !== draft.id));
    setToast({ kind: "saved", text: "Draft deleted." });
    const remaining = items.filter((item) => item.id !== draft.id && item.status === "draft");
    const fallback = remaining[0] ?? items.find((item) => item.status === "created") ?? items[0] ?? null;
    const next = new URLSearchParams(searchParams);
    if (fallback) {
      next.set("tab", fallback.status);
      next.set("invite", fallback.id);
    } else {
      next.set("tab", "draft");
      next.delete("invite");
    }
    setSearchParams(next);
  };

  const activeCount = counts.find((entry) => entry.stage === activeTab)?.count ?? 0;

  return (
    <main className="min-h-svh bg-[rgb(var(--bg))] text-[rgb(var(--text))]">
      <div className="mx-auto flex min-h-svh max-w-[1520px] flex-col px-4 py-4 sm:px-6 lg:px-8">
        <header className="mb-5 flex flex-col gap-4 rounded-[28px] border border-black/5 bg-white/85 px-5 py-5 shadow-[0_18px_60px_-36px_rgba(40,24,12,0.28)] backdrop-blur-xl sm:px-6 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-2xl">
            <p className="text-[11px] font-semibold uppercase tracking-[0.32em] text-[rgb(142_109_64)]">
              My Invites
            </p>
            <h1 className="mt-2 font-display text-[clamp(2.2rem,4vw,4.3rem)] leading-[0.95] tracking-[-0.04em] text-[rgb(27_23_20)]">
              One place for drafts, delivery, and response tracking.
            </h1>
            <p className="mt-3 max-w-xl text-[15px] leading-7 text-[rgb(80_71_66)]">
              Manage every invitation from the first draft to the final reply.
              Publish a draft and it immediately moves into the live list.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <HeaderStat label="Drafts" value={counts.find((c) => c.stage === "draft")?.count ?? 0} />
            <HeaderStat label="Published" value={items.length - (counts.find((c) => c.stage === "draft")?.count ?? 0)} />
            <Link
              to="/i/date-night-lucia"
              className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-[rgb(26_23_21)] px-4 py-3 text-sm font-semibold text-white shadow-[0_14px_30px_-16px_rgba(26,23,21,0.7)] transition hover:-translate-y-0.5"
            >
              Open preview
              <ArrowIcon className="h-4 w-4" />
            </Link>
          </div>
        </header>

        <div className="grid flex-1 gap-5 lg:grid-cols-[minmax(0,1.02fr)_minmax(360px,0.78fr)]">
          <section className="flex min-h-0 flex-col overflow-hidden rounded-[30px] border border-black/5 bg-[rgba(255,255,255,0.72)] shadow-[0_22px_70px_-42px_rgba(35,22,15,0.28)] backdrop-blur-xl">
            <div className="border-b border-black/5 px-4 py-4 sm:px-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[rgb(130_103_63)]">
                    Library
                  </p>
                  <h2 className="mt-1 font-display text-2xl font-semibold text-[rgb(27_23_20)]">
                    Status overview
                  </h2>
                </div>
                <div className="rounded-full border border-black/8 bg-white px-3 py-1 text-xs font-medium text-[rgb(94_82_72)]">
                  {activeCount} item{activeCount === 1 ? "" : "s"} in {INVITE_STAGE_LABELS[activeTab].toLowerCase()}
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {stageTabCopy.map(({ stage, label }) => {
                  const isActive = stage === activeTab;
                  const count = counts.find((entry) => entry.stage === stage)?.count ?? 0;
                  return (
                    <button
                      key={stage}
                      type="button"
                      onClick={() => selectTab(stage)}
                      className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition ${
                        isActive
                          ? "border-[rgb(26_23_21)] bg-[rgb(26_23_21)] text-white shadow-[0_14px_30px_-18px_rgba(26,23,21,0.7)]"
                          : "border-black/8 bg-white text-[rgb(86_76_68)] hover:bg-[rgb(251_248_244)]"
                      }`}
                    >
                      {label}
                      <span
                        className={`rounded-full px-2 py-0.5 text-[11px] ${
                          isActive ? "bg-white/15 text-white" : "bg-[rgb(245_241_235)] text-[rgb(99_89_79)]"
                        }`}
                      >
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-3 py-4 sm:px-4">
              <div className="grid gap-3">
                {filteredItems.map((invite) => (
                  <InviteCard
                    key={invite.id}
                    invite={invite}
                    active={invite.id === selectedItem?.id}
                    onClick={() => selectInvite(invite.id)}
                  />
                ))}
                {filteredItems.length === 0 && (
                  <EmptyState stage={activeTab} onJump={() => selectTab("draft")} />
                )}
              </div>
            </div>
          </section>

          <section className="min-h-0 overflow-hidden rounded-[30px] border border-black/5 bg-[rgb(251_248_244)] shadow-[0_22px_70px_-42px_rgba(35,22,15,0.28)]">
            {selectedItem ? (
              selectedItem.kind === "draft" ? (
                <DraftDetailView
                  invite={selectedItem}
                  editing={!!editingDrafts[selectedItem.id]}
                  onToggleEdit={() =>
                    setEditingDrafts((current) => ({
                      ...current,
                      [selectedItem.id]: !current[selectedItem.id]
                    }))
                  }
                  onChange={(patch) =>
                    updateInvite(selectedItem.id, (item) => ({
                      ...(item as DraftInvite),
                      ...patch
                    }))
                  }
                  onPublish={() => publishDraft(selectedItem)}
                  onDelete={() => deleteDraft(selectedItem)}
                  toast={toast}
                />
              ) : (
                <PublishedInviteDetailView
                  invite={selectedItem}
                  toast={toast}
                  onToast={(nextToast) => setToast(nextToast)}
                />
              )
            ) : (
              <BlankDetail />
            )}
          </section>
        </div>
      </div>
    </main>
  );
}

function DraftDetailView({
  invite,
  editing,
  onToggleEdit,
  onChange,
  onPublish,
  onDelete,
  toast
}: {
  invite: DraftInvite;
  editing: boolean;
  onToggleEdit: () => void;
  onChange: (patch: Partial<DraftInvite>) => void;
  onPublish: () => void;
  onDelete: () => void;
  toast: ToastState;
}) {
  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-black/5 px-5 py-5 sm:px-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-xl">
            <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[rgb(136_112_82)]">
              Draft detail
            </p>
            <h2 className="mt-2 font-display text-[clamp(1.9rem,3vw,3.1rem)] leading-[0.97] tracking-[-0.04em] text-[rgb(30_24_19)]">
              {invite.title}
            </h2>
            <p className="mt-2 text-sm leading-6 text-[rgb(93_81_70)]">
              Prepare the invitation before publishing. When it goes live, it leaves Drafts and becomes a published invite.
            </p>
          </div>
          <StatusBadge invite={invite} />
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <ActionButton onClick={onToggleEdit} icon={<PencilIcon />} label="Edit" />
          <ActionButton onClick={onPublish} icon={<SparklesIcon />} label="Generate invitation" primary />
          <ActionButton onClick={onDelete} icon={<TrashIcon />} label="Delete draft" destructive />
        </div>

        {toast.kind !== "idle" && (
          <p className="mt-3 text-sm font-medium text-[rgb(116_90_48)]">{toast.text}</p>
        )}
      </div>

      <div className="grid flex-1 gap-5 p-5 sm:p-6 xl:grid-cols-[minmax(0,1.02fr)_minmax(320px,0.98fr)]">
        <PreviewPanel invite={invite} />

        <div className="space-y-4">
          <InfoCard title="Core fields" subtitle="Theme, animation, media, recipient and title." compact>
            <LabeledInput
              label="Title"
              value={invite.title}
              onChange={(value) => onChange({ title: value })}
              editing={editing}
            />
            <LabeledInput
              label="Recipient"
              value={invite.recipientName}
              onChange={(value) => onChange({ recipientName: value })}
              editing={editing}
            />
            <LabeledSelect
              label="Theme"
              value={invite.theme}
              options={Object.entries(THEME_LABELS).map(([value, label]) => ({ value, label }))}
              onChange={(value) => onChange({ theme: value as DraftInvite["theme"] })}
              editing={editing}
            />
            <LabeledSelect
              label="Animation"
              value={invite.animationMode}
              options={Object.entries(ANIMATION_LABELS).map(([value, label]) => ({ value, label }))}
              onChange={(value) => onChange({ animationMode: value as DraftInvite["animationMode"] })}
              editing={editing}
            />
          </InfoCard>

          <InfoCard title="Media" subtitle="Show the creative assets attached to this draft." compact>
            <FieldLine label="Media type" value={invite.media.kind} />
            <FieldLine label="Media bundle" value={invite.media.label} />
            <FieldLine label="Details" value={invite.media.detail} />
          </InfoCard>

          <InfoCard title="Schedule" subtitle="Availability and publish window." compact>
            <FieldLine label="Created at" value={formatDateTime(invite.createdAt)} />
            <FieldLine label="Expires at" value={formatDateTime(invite.expiresAt)} />
            <FieldLine label="Premium" value={invite.premium ? "Uses premium features" : "Standard draft"} />
          </InfoCard>
        </div>
      </div>
    </div>
  );
}

function PublishedInviteDetailView({
  invite,
  toast,
  onToast
}: {
  invite: PublishedInvite;
  toast: ToastState;
  onToast: (state: ToastState) => void;
}) {
  const [qrDownloadUrl, setQrDownloadUrl] = useState<string>("");

  useEffect(() => {
    const svg = renderQrSvg(invite.publicUrl);
    const blob = new Blob([svg], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    setQrDownloadUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [invite.publicUrl]);

  const shareText = `${invite.title} for ${invite.recipientName}`;

  const copyLink = async () => {
    await navigator.clipboard.writeText(invite.publicUrl);
    onToast({ kind: "copied", text: "Public link copied." });
  };

  const shareNative = async () => {
    if (navigator.share) {
      await navigator.share({
        title: invite.title,
        text: shareText,
        url: invite.publicUrl
      });
      onToast({ kind: "shared", text: "Share sheet opened." });
      return;
    }
    await copyLink();
  };

  const openPreview = () => {
    window.open(invite.publicUrl, "_blank", "noopener,noreferrer");
    onToast({ kind: "saved", text: "Opened public preview." });
  };

  const downloadQr = () => {
    const svg = renderQrSvg(invite.publicUrl);
    const blob = new Blob([svg], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${invite.slug}-qr.svg`;
    a.click();
    URL.revokeObjectURL(url);
    onToast({ kind: "downloaded", text: "QR downloaded." });
  };

  const openShareTarget = (kind: "email" | "sms" | "whatsapp") => {
    const encodedText = encodeURIComponent(`${shareText}\n${invite.publicUrl}`);
    if (kind === "email") {
      window.location.href = `mailto:?subject=${encodeURIComponent(invite.title)}&body=${encodedText}`;
    }
    if (kind === "sms") {
      window.location.href = `sms:?&body=${encodedText}`;
    }
    if (kind === "whatsapp") {
      window.open(`https://wa.me/?text=${encodedText}`, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="border-b border-black/5 px-5 py-5 sm:px-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-2xl">
            <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-[rgb(136_112_82)]">
              Published invitation
            </p>
            <h2 className="mt-2 font-display text-[clamp(1.9rem,3vw,3.1rem)] leading-[0.97] tracking-[-0.04em] text-[rgb(30_24_19)]">
              {invite.title}
            </h2>
            <p className="mt-2 text-sm leading-6 text-[rgb(93_81_70)]">
              Share the public link, open the web preview, or download the QR for offline sharing.
            </p>
          </div>
          <div className="flex flex-col items-end gap-2">
            <StatusBadge invite={invite} />
            {invite.responseStatus && (
              <ResponseBadge invite={invite} />
            )}
          </div>
        </div>

        {toast.kind !== "idle" && (
          <p className="mt-3 text-sm font-medium text-[rgb(116_90_48)]">{toast.text}</p>
        )}
      </div>

      <div className="grid flex-1 gap-5 p-5 sm:p-6 xl:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-5">
          <InfoCard title="Public link" subtitle="The URL that anyone can open." compact>
            <div className="flex flex-col gap-3 sm:flex-row">
              <div className="flex-1 break-all rounded-2xl border border-black/8 bg-white px-4 py-3 text-sm leading-6 text-[rgb(52_44_38)]">
                {invite.publicUrl}
              </div>
              <ActionButton onClick={copyLink} icon={<CopyIcon />} label="Copy link" />
            </div>
          </InfoCard>

          <InfoCard title="Share tools" subtitle="Every shortcut you need to send the invite." compact>
            <div className="grid gap-2 sm:grid-cols-2">
              <ActionButton onClick={shareNative} icon={<ShareIcon />} label="Share" />
              <ActionButton onClick={openPreview} icon={<WebIcon />} label="Open web preview" />
              <ActionButton onClick={() => openShareTarget("email")} icon={<MailIcon />} label="Email" />
              <ActionButton onClick={() => openShareTarget("sms")} icon={<MessageIcon />} label="SMS" />
              <ActionButton onClick={() => openShareTarget("whatsapp")} icon={<WhatsAppIcon />} label="WhatsApp/share" />
              <ActionButton onClick={downloadQr} icon={<DownloadIcon />} label="Download QR" />
            </div>

            <div className="mt-3 rounded-3xl border border-dashed border-[rgb(204_165_96_/_0.45)] bg-[rgb(255_249_236)] p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-[rgb(104_78_28)]">Prank / mystery share</p>
                  <p className="mt-1 text-xs leading-5 text-[rgb(128_97_43)]">
                    Pro placeholder for a concealed send mode with playful reveal logic.
                  </p>
                </div>
                <span className="rounded-full bg-[rgb(204_165_96_/_0.18)] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-[rgb(143_105_27)]">
                  Pro
                </span>
              </div>
              <button
                type="button"
                disabled
                className="mt-4 inline-flex cursor-not-allowed items-center gap-2 rounded-full border border-black/8 bg-white px-4 py-2 text-sm font-semibold text-[rgb(126_98_52)] opacity-70"
              >
                <LockIcon />
                Mystery send coming soon
              </button>
            </div>
          </InfoCard>

          {invite.responseStatus && (
            <InfoCard title="Response status" subtitle="What the guest answered." compact>
              <FieldLine label="Answer" value={invite.responseStatus === "yes" ? "Yes" : "No"} />
              <FieldLine label="Response time" value={formatDateTime(invite.responseAt)} />
              <FieldLine label="Note" value={invite.responseLabel ?? "Response recorded."} />
            </InfoCard>
          )}
        </div>

        <div className="space-y-5">
          <PreviewPanel invite={invite} />

          <InfoCard title="Premium" subtitle="Small upgrade card for advanced tools." compact>
            <div className="rounded-[24px] bg-[linear-gradient(135deg,rgba(255,255,255,0.95),rgba(255,244,226,0.95))] p-4">
              <p className="text-sm font-semibold text-[rgb(88_63_33)]">Unlock premium automations</p>
              <p className="mt-1 text-sm leading-6 text-[rgb(102_77_47)]">
                Add branded links, richer response flows, and mystery-share variations for this invite.
              </p>
              <button
                type="button"
                className="mt-4 inline-flex items-center gap-2 rounded-full border border-[rgb(204_165_96_/_0.35)] bg-[rgb(26_23_21)] px-4 py-2 text-sm font-semibold text-white transition hover:-translate-y-0.5"
              >
                <SparklesIcon />
                Upgrade premium
              </button>
            </div>
          </InfoCard>

          <InfoCard title="Metadata" subtitle="Useful bits for publishing and support." compact>
            <FieldLine label="Created at" value={formatDateTime(invite.createdAt)} />
            <FieldLine label="Slug" value={invite.slug} />
            <FieldLine label="Theme" value={THEME_LABELS[invite.theme]} />
            <FieldLine label="Animation" value={ANIMATION_LABELS[invite.animationMode]} />
          </InfoCard>
        </div>
      </div>

      {qrDownloadUrl && (
        <a className="sr-only" href={qrDownloadUrl} download={`${invite.slug}-qr.svg`}>
          hidden qr download
        </a>
      )}
    </div>
  );
}

function PreviewPanel({ invite }: { invite: MyInvite }) {
  return (
    <InfoCard title="Mini preview" subtitle="Placeholder invitation surface." compact>
      <div className="overflow-hidden rounded-[28px] border border-black/5 bg-white p-4">
        <div className={`rounded-[24px] bg-gradient-to-br ${themeTone[invite.theme] || themeTone.minimal} p-5`}>
          <div className="flex items-start justify-between gap-3">
            <div className="max-w-[75%]">
              <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[rgb(111_88_52)]">
                Mini preview
              </p>
              <h3 className="mt-2 font-display text-[clamp(1.4rem,2vw,2rem)] leading-[1] text-[rgb(29_22_18)]">
                {invite.title}
              </h3>
              <p className="mt-2 text-sm text-[rgb(84_71_61)]">
                For {invite.recipientName}
              </p>
            </div>
            <div className="rounded-full bg-white/75 px-3 py-1 text-[11px] font-semibold text-[rgb(92_72_45)] shadow-sm">
              {invite.kind === "draft" ? "Draft" : INVITE_STAGE_LABELS[invite.status]}
            </div>
          </div>

          <div className="mt-5 rounded-[22px] border border-black/8 bg-white/80 p-4 shadow-[0_12px_30px_-18px_rgba(36,22,12,0.35)]">
            <div className="grid grid-cols-[108px_minmax(0,1fr)] gap-4">
              <div className="overflow-hidden rounded-[18px] border border-black/6 bg-[linear-gradient(160deg,rgba(255,255,255,0.8),rgba(228,238,231,0.95))] p-3">
                <div className="flex h-full min-h-[106px] flex-col justify-between rounded-[14px] bg-white/65 p-3">
                  <div className="flex items-center justify-between">
                    <span className="h-3 w-3 rounded-full bg-[rgb(204_165_96)]" />
                    <span className="text-[9px] font-semibold uppercase tracking-[0.25em] text-[rgb(110_103_94)]">
                      Preview
                    </span>
                  </div>
                  <div>
                    <div className="h-9 w-14 rounded-xl bg-[linear-gradient(135deg,rgba(191,140,95,0.45),rgba(255,255,255,0.8))]" />
                    <div className="mt-2 h-2 w-10 rounded-full bg-black/10" />
                  </div>
                </div>
              </div>
              <div className="flex flex-col justify-between">
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[rgb(120_97_73)]">
                    {THEME_LABELS[invite.theme]}
                  </p>
                  <p className="mt-2 text-sm leading-6 text-[rgb(63_53_46)]">
                    {invite.media.label}
                  </p>
                  <p className="mt-1 text-xs leading-5 text-[rgb(97_87_78)]">
                    {invite.media.detail}
                  </p>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Badge label={ANIMATION_LABELS[invite.animationMode]} />
                  {invite.premium && <Badge label="Premium" tone="gold" />}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between rounded-[18px] border border-black/6 bg-white/75 px-4 py-3">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[rgb(125_99_63)]">
                Recipient
              </p>
              <p className="mt-1 text-sm font-semibold text-[rgb(38_31_25)]">{invite.recipientName}</p>
            </div>
            <div className="text-right">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[rgb(125_99_63)]">
                Created
              </p>
              <p className="mt-1 text-sm font-medium text-[rgb(38_31_25)]">{formatDateTime(invite.createdAt)}</p>
            </div>
          </div>
        </div>
      </div>
    </InfoCard>
  );
}

function InviteCard({
  invite,
  active,
  onClick
}: {
  invite: MyInvite;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group w-full rounded-[24px] border p-3 text-left transition ${
        active
          ? "border-[rgb(26_23_21)] bg-white shadow-[0_16px_40px_-26px_rgba(26,23,21,0.45)]"
          : "border-black/6 bg-white/72 hover:-translate-y-0.5 hover:border-black/10 hover:bg-white hover:shadow-[0_16px_36px_-28px_rgba(26,23,21,0.28)]"
      }`}
    >
      <div className="grid gap-3 sm:grid-cols-[118px_minmax(0,1fr)]">
        <div className={`rounded-[20px] bg-gradient-to-br ${themeTone[invite.theme] || themeTone.minimal} p-3`}>
          <div className="flex h-full min-h-[100px] flex-col justify-between rounded-[16px] border border-white/65 bg-white/70 p-3">
            <div className="flex items-center justify-between">
              <span className="text-[10px] font-semibold uppercase tracking-[0.24em] text-[rgb(123_103_83)]">
                Preview
              </span>
              <MiniDot active={active} />
            </div>
            <div>
              <div className="h-8 w-12 rounded-[10px] bg-[linear-gradient(135deg,rgba(255,255,255,0.85),rgba(191,140,95,0.35))]" />
              <div className="mt-2 h-2 w-16 rounded-full bg-black/10" />
            </div>
          </div>
        </div>

        <div className="flex min-w-0 flex-col justify-between gap-3 py-1">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="truncate font-display text-[1.25rem] leading-tight text-[rgb(28_23_18)]">
                  {invite.title}
                </h3>
                {invite.premium && <Badge label="Premium" tone="gold" />}
              </div>
              <p className="mt-1 text-sm text-[rgb(94_82_71)]">
                For {invite.recipientName}
              </p>
            </div>
            <span className={`shrink-0 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] ${stageTone[invite.status]}`}>
              {INVITE_STAGE_LABELS[invite.status]}
            </span>
          </div>

          <div className="grid gap-2 text-sm text-[rgb(95_84_73)] sm:grid-cols-2">
            <CardMeta label="Created at" value={formatDateTime(invite.createdAt)} />
            <CardMeta label="Expires at" value={formatDateTime(invite.expiresAt)} />
          </div>
        </div>
      </div>
    </button>
  );
}

function BlankDetail() {
  return (
    <div className="flex h-full items-center justify-center p-10 text-center">
      <div className="max-w-sm">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-white text-[rgb(130_103_63)] shadow-sm">
          <SparklesIcon />
        </div>
        <h2 className="mt-4 font-display text-3xl font-semibold text-[rgb(28_23_18)]">
          Select an invite
        </h2>
        <p className="mt-3 text-sm leading-6 text-[rgb(92_82_72)]">
          Choose a draft or published invite from the list to inspect the detail pane.
        </p>
      </div>
    </div>
  );
}

function EmptyState({
  stage,
  onJump
}: {
  stage: InviteStage;
  onJump: () => void;
}) {
  return (
    <div className="rounded-[26px] border border-dashed border-black/8 bg-white px-5 py-8 text-center">
      <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[rgb(141_115_76)]">
        No invites here
      </p>
      <h3 className="mt-2 font-display text-2xl font-semibold text-[rgb(28_23_18)]">
        {INVITE_STAGE_LABELS[stage]} is empty
      </h3>
      <p className="mt-2 text-sm leading-6 text-[rgb(93_82_71)]">
        Switch back to drafts and publish one, or try a different status filter.
      </p>
      <button
        type="button"
        onClick={onJump}
        className="mt-4 inline-flex items-center gap-2 rounded-full bg-[rgb(26_23_21)] px-4 py-2 text-sm font-semibold text-white"
      >
        Go to drafts
        <ArrowIcon className="h-4 w-4" />
      </button>
    </div>
  );
}

function InfoCard({
  title,
  subtitle,
  compact = false,
  children
}: {
  title: string;
  subtitle: string;
  compact?: boolean;
  children: ReactNode;
}) {
  return (
    <div className={`rounded-[28px] border border-black/5 bg-white ${compact ? "p-4" : "p-5"}`}>
      <div className="mb-4">
        <h3 className="font-display text-2xl font-semibold text-[rgb(29_23_18)]">{title}</h3>
        <p className="mt-1 text-sm leading-6 text-[rgb(97_86_74)]">{subtitle}</p>
      </div>
      <div className="space-y-3">{children}</div>
    </div>
  );
}

function FieldLine({ label, value }: { label: string; value?: string }) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-2xl bg-[rgb(251_248_244)] px-4 py-3">
      <span className="text-sm font-medium text-[rgb(109_94_79)]">{label}</span>
      <span className="max-w-[60%] text-right text-sm font-semibold text-[rgb(35_28_22)]">{value || "—"}</span>
    </div>
  );
}

function LabeledInput({
  label,
  value,
  onChange,
  editing
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  editing: boolean;
}) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-medium text-[rgb(92_80_68)]">{label}</span>
      {editing ? (
        <input
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="w-full rounded-2xl border border-black/8 bg-[rgb(251_248_244)] px-4 py-3 text-sm text-[rgb(36_29_22)] outline-none transition focus:border-[rgb(204_165_96)] focus:bg-white"
        />
      ) : (
        <div className="rounded-2xl border border-black/5 bg-[rgb(251_248_244)] px-4 py-3 text-sm font-semibold text-[rgb(36_29_22)]">
          {value}
        </div>
      )}
    </label>
  );
}

function LabeledSelect<T extends string>({
  label,
  value,
  options,
  onChange,
  editing
}: {
  label: string;
  value: T;
  options: { value: string; label: string }[];
  onChange: (value: T) => void;
  editing: boolean;
}) {
  return (
    <label className="block">
      <span className="mb-2 block text-sm font-medium text-[rgb(92_80_68)]">{label}</span>
      {editing ? (
        <select
          value={value}
          onChange={(event) => onChange(event.target.value as T)}
          className="w-full rounded-2xl border border-black/8 bg-[rgb(251_248_244)] px-4 py-3 text-sm text-[rgb(36_29_22)] outline-none transition focus:border-[rgb(204_165_96)] focus:bg-white"
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      ) : (
        <div className="rounded-2xl border border-black/5 bg-[rgb(251_248_244)] px-4 py-3 text-sm font-semibold text-[rgb(36_29_22)]">
          {options.find((option) => option.value === value)?.label ?? value}
        </div>
      )}
    </label>
  );
}

function CardMeta({ label, value }: { label: string; value?: string }) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[rgb(135_111_78)]">
        {label}
      </p>
      <p className="mt-1 text-sm font-medium text-[rgb(42_34_29)]">{value || "—"}</p>
    </div>
  );
}

function HeaderStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-[20px] border border-black/8 bg-white px-4 py-3">
      <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[rgb(137_112_80)]">
        {label}
      </p>
      <p className="mt-1 font-display text-2xl font-semibold text-[rgb(29_24_19)]">{value}</p>
    </div>
  );
}

function ActionButton({
  onClick,
  label,
  icon,
  primary = false,
  destructive = false
}: {
  onClick: () => void;
  label: string;
  icon: ReactNode;
  primary?: boolean;
  destructive?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full border px-4 py-3 text-sm font-semibold transition hover:-translate-y-0.5 ${
        primary
          ? "border-[rgb(26_23_21)] bg-[rgb(26_23_21)] text-white"
          : destructive
            ? "border-[rgb(214_102_120_/_0.18)] bg-[rgb(255_244_245)] text-[rgb(161_64_87)]"
            : "border-black/8 bg-white text-[rgb(55_45_39)]"
      }`}
    >
      {icon}
      {label}
    </button>
  );
}

function StatusBadge({ invite }: { invite: MyInvite }) {
  return (
    <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] ${stageTone[invite.status]}`}>
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {INVITE_STAGE_LABELS[invite.status]}
    </span>
  );
}

function ResponseBadge({ invite }: { invite: PublishedInvite }) {
  const label = invite.responseStatus === "yes" ? "Responded yes" : "Responded no";
  return (
    <span className="inline-flex items-center gap-2 rounded-full bg-[rgb(214_102_120_/_0.12)] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-[rgb(160_61_79)]">
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {label}
    </span>
  );
}

function Badge({ label, tone = "neutral" }: { label: string; tone?: "neutral" | "gold" }) {
  return (
    <span
      className={`inline-flex rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] ${
        tone === "gold"
          ? "bg-[rgb(204_165_96_/_0.16)] text-[rgb(143_105_27)]"
          : "bg-[rgb(245_241_235)] text-[rgb(101_89_77)]"
      }`}
    >
      {label}
    </span>
  );
}

function MiniDot({ active }: { active: boolean }) {
  return <span className={`h-2.5 w-2.5 rounded-full ${active ? "bg-[rgb(26_23_21)]" : "bg-black/20"}`} />;
}

function ArrowIcon({ className = "" }: { className?: string }) {
  return (
    <svg viewBox="0 0 16 16" fill="none" className={className} aria-hidden="true">
      <path d="M3.5 8h9" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M8.2 4.5 12 8l-3.8 3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SparklesIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" className="h-4 w-4">
      <path d="M8 1.8 9.3 5l3.2 1.3-3.2 1.3L8 10.8 6.7 7.6 3.5 6.3 6.7 5 8 1.8Z" fill="currentColor" />
      <path d="M12.7 9.5 13.3 11l1.5.6-1.5.6-.6 1.5-.6-1.5-1.5-.6 1.5-.6.6-1.5Z" fill="currentColor" opacity=".8" />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" className="h-4 w-4">
      <path d="M10.9 2.4 13.6 5.1 6 12.7 2.9 13.2l.5-3.1 7.5-7.7Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M9.4 3.9 12.1 6.6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" className="h-4 w-4">
      <path d="M6 2.8h4m-6.2 2h8.4M5.7 4.8l.5 8.4h3.6l.5-8.4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M7 6.1v4.5M9 6.1v4.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function CopyIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" className="h-4 w-4">
      <path d="M5.2 4.5h5.1a1.7 1.7 0 0 1 1.7 1.7v5.1a1.7 1.7 0 0 1-1.7 1.7H5.2a1.7 1.7 0 0 1-1.7-1.7V6.2a1.7 1.7 0 0 1 1.7-1.7Z" stroke="currentColor" strokeWidth="1.4" />
      <path d="M4.2 11.7H3.6a1.4 1.4 0 0 1-1.4-1.4V4.8a1.4 1.4 0 0 1 1.4-1.4h5.5a1.4 1.4 0 0 1 1.4 1.4v.7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function ShareIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" className="h-4 w-4">
      <path d="M11.2 4.2a1.6 1.6 0 1 0 0-3.2 1.6 1.6 0 0 0 0 3.2ZM4.8 9a1.6 1.6 0 1 0 0-3.2A1.6 1.6 0 0 0 4.8 9Zm6.4 6a1.6 1.6 0 1 0 0-3.2 1.6 1.6 0 0 0 0 3.2Z" fill="currentColor" />
      <path d="m6 6.1 3.9-1.8m0 7.4L6 9.9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
  );
}

function WebIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" className="h-4 w-4">
      <path d="M2.8 4.2h10.4v7.6H2.8V4.2Z" stroke="currentColor" strokeWidth="1.4" />
      <path d="M2.8 6.5h10.4" stroke="currentColor" strokeWidth="1.4" />
      <path d="M5.2 2.8v2.4M10.8 2.8v2.4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function MailIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" className="h-4 w-4">
      <path d="M2.6 4.2h10.8v7.6H2.6V4.2Z" stroke="currentColor" strokeWidth="1.4" />
      <path d="m3.2 4.8 4.8 3.7 4.8-3.7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function MessageIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" className="h-4 w-4">
      <path d="M3 3.4h10v6.4H7.2L4.3 12.1V9.8H3V3.4Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </svg>
  );
}

function WhatsAppIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" className="h-4 w-4">
      <path d="M8 2.3a5.7 5.7 0 0 0-4.9 8.5l-.8 2.9 3-.8A5.7 5.7 0 1 0 8 2.3Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
      <path d="M6.2 5.5c.1-.2.2-.3.4-.3h.4c.1 0 .3 0 .4.3l.6 1.3c.1.2.1.4-.1.5l-.4.4c-.1.1-.1.3 0 .4.3.6.8 1.1 1.4 1.4.1.1.3.1.4 0l.4-.4c.2-.2.3-.2.5-.1l1.3.6c.2.1.3.2.3.4 0 .2 0 .3-.2.5-.2.4-.7.8-1.2.8-2.1 0-4.3-2.2-4.3-4.3 0-.5.4-1 .8-1.2Z" fill="currentColor" opacity=".9" />
    </svg>
  );
}

function DownloadIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" className="h-4 w-4">
      <path d="M8 2.6v7M5.6 7.6 8 10l2.4-2.4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M3 12.2h10" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

function LockIcon() {
  return (
    <svg viewBox="0 0 16 16" fill="none" aria-hidden="true" className="h-4 w-4">
      <path d="M5.2 6.7V5.1a2.8 2.8 0 1 1 5.6 0v1.6" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
      <path d="M4.4 6.7h7.2v6H4.4v-6Z" stroke="currentColor" strokeWidth="1.4" />
    </svg>
  );
}

function normalizeStage(value: string | null): InviteStage {
  if (value && INVITE_STAGE_ORDER.includes(value as InviteStage)) {
    return value as InviteStage;
  }
  return "draft";
}

function formatDateTime(value?: string) {
  if (!value) return undefined;
  const date = new Date(value);
  return new Intl.DateTimeFormat("en", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit"
  }).format(date);
}

function slugify(value: string) {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-")
    .slice(0, 36);
}

function renderQrSvg(value: string) {
  const size = 21;
  const seed = hashString(value);
  const cells: string[] = [];

  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const finder =
        insideFinder(x, y, 0, 0) ||
        insideFinder(x, y, size - 7, 0) ||
        insideFinder(x, y, 0, size - 7);
      const shouldDraw = finder || isFilledCell(x, y, seed);
      if (!shouldDraw) continue;
      cells.push(`<rect x="${x}" y="${y}" width="1" height="1" rx="0.12" fill="#1a1715" />`);
    }
  }

  return `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" shape-rendering="crispEdges">
  <rect width="${size}" height="${size}" fill="#fffdf8" rx="2" />
  <g transform="translate(1 1)">
    ${cells.join("")}
  </g>
</svg>`;
}

function hashString(value: string) {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
}

function isFilledCell(x: number, y: number, seed: number) {
  const n = Math.abs(seed + x * 13 + y * 17 + x * y * 19);
  return n % 3 === 0 || n % 7 === 0;
}

function insideFinder(x: number, y: number, ox: number, oy: number) {
  return x >= ox && x < ox + 7 && y >= oy && y < oy + 7;
}
